import { Text } from '@nike/eds';
import { useQuery } from '@tanstack/react-query';
import { explainSearch } from 'clientAPI/kingpin';
import Loader from 'components/Loader';
import React, { Suspense } from 'react';
import { useRevalidator } from 'react-router-dom';

// eslint-disable-next-line import/no-named-as-default
import ExplainRecallDetails, {
  JsonViewerCustom,
  Title,
} from './ExplainRecallDetails';

type SearchProps = {
  searchTerms: string;
  language?: string; //'zh-Hans' | 'en';
  marketplace?: string;
  consumerChannelId?: string;
  searchOptions: { [key: string]: string };
  sort?: string;
};

function useExplainSearch(props: SearchProps) {
  const revalidator = useRevalidator();

  return useQuery(['kingpin-explain', props], explainSearch(), {
    // 5 minutes stale time
    staleTime: 5 * 60 * 1000, // 5 minutes
    suspense: true,
    enabled: !!props.searchTerms && !!props.consumerChannelId,
    onError: (err: keyable) => err.status === 401 && revalidator.revalidate(),
  });
}

// eslint-disable-next-line import/no-unused-modules
export function Explain(props: SearchProps) {
  return (
    <>
      <Text font='display-5' as='h3' className='tw-my-2'>
        Explain
      </Text>
      {!props.searchTerms ? (
        <Text>Missing searchTerms</Text>
      ) : (
        <ExplainShell>
          <ExplainCore {...props} />
        </ExplainShell>
      )}
    </>
  );
}

function ExplainShell({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Loader />}>{children}</Suspense>;
}

function ExplainCore(props: SearchProps) {
  const { data } = useExplainSearch(props);

  return (
    <div>
      {data.productRankingExplain?.rule?.name ? (
        <section className='tw-py-2'>
          <Title>Ranking Rule Info</Title>
          <div className='tw-flex tw-flex-wrap tw-gap-2 tw-items-center'>
            Name:{' '}
            <a
              href={`/ranking/edit-rule/${data.productRankingExplain?.rule?._id}`}
              target='_blank'
              rel='noopener noreferrer'
              className='tw-px-3 tw-py-1 tw-bg-blue-100 tw-text-blue-800 tw-rounded-full tw-text-sm tw-no-underline hover:tw-underline'
            >
              {data.productRankingExplain?.rule?.name}
            </a>
          </div>
        </section>
      ) : null}
      {data.queryProcessorMultiStageResponse?.map(
        (data: any, index: number) => (
          // eslint-disable-next-line react/jsx-no-comment-textnodes, react/jsx-key
          <section className='tw-py-2'>
            <Title>
              Multi Path Recall Details For Stage: {data.result.stageName}
            </Title>
            <ExplainRecallDetails
              explainRecallData={data}
              explainRecallIndex={index}
              defaultShowRecallDetailsFlag={false}
            />
          </section>
        ),
      )}
      {data.result ? (
        <section className='tw-py-2'>
          <Title>Single Path Recall Details</Title>
          <ExplainRecallDetails
            explainRecallData={data}
            explainRecallIndex={0}
            defaultShowRecallDetailsFlag={true}
          />
        </section>
      ) : null}
    </div>
  );
}
